/**
 * Woke Product Gallery
 *
 * Transform-based slider — avoids Dawn's display:none on non-active slides
 * (section-main-product.css hides .product--thumbnail_slider slides that
 * don't have .is-active). Instead we show all slides, lay them in a flex
 * row, and drive the visible position by setting translateX on the ul.
 *
 * Dawn's media-gallery.js still manages:
 *   - thumbnail aria-current state
 *   - variant image switching (is-active class)
 *   - video/model play
 *   - zoom modal
 *
 * We hook in via MutationObserver: whenever Dawn adds .is-active to a
 * slide (from thumbnail click, variant change, etc.) we apply the
 * corresponding translateX. Our arrows then just click the matching
 * thumbnail button and let the whole chain run naturally.
 */

(function () {
  'use strict';

  var MQ_DESKTOP = window.matchMedia('(min-width: 750px)');

  function initGallery(mediaGallery) {
    var wrap = mediaGallery.querySelector('[data-woke-gallery]');
    if (!wrap || wrap.dataset.wokeInit) return;
    wrap.dataset.wokeInit = '1';

    var viewerSlider = mediaGallery.querySelector('[id^="GalleryViewer"]');
    var thumbSlider  = mediaGallery.querySelector('[id^="GalleryThumbnails"]');
    var sliderList   = viewerSlider && viewerSlider.querySelector('[id^="Slider-Gallery"]');
    var prevArrow    = viewerSlider && viewerSlider.querySelector('[data-woke-prev]');
    var nextArrow    = viewerSlider && viewerSlider.querySelector('[data-woke-next]');

    if (!sliderList || !prevArrow || !nextArrow) return;

    var slides = Array.from(sliderList.querySelectorAll('.product__media-item'));
    if (slides.length < 2) return;

    var currentIndex  = 0;
    var isAnimating   = false;

    /* ── Core: apply CSS transform ─────────────────────────────── */

    function viewerWidth() {
      // Width of the clipping wrapper, not the ul
      return viewerSlider.offsetWidth || sliderList.offsetWidth;
    }

    function applyTransform(index, animate) {
      if (!MQ_DESKTOP.matches) return;
      var px = index * viewerWidth();
      if (animate === false) {
        // Instant jump (used on init)
        sliderList.style.transition = 'none';
        sliderList.style.transform  = 'translateX(-' + px + 'px)';
        // Re-enable transitions after next paint
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            sliderList.style.transition = '';
          });
        });
      } else {
        sliderList.style.transform = 'translateX(-' + px + 'px)';
      }
    }

    /* ── Arrow visibility ──────────────────────────────────────── */

    function updateArrows() {
      if (currentIndex <= 0) {
        prevArrow.setAttribute('hidden', '');
      } else {
        prevArrow.removeAttribute('hidden');
      }
      if (currentIndex >= slides.length - 1) {
        nextArrow.setAttribute('hidden', '');
      } else {
        nextArrow.removeAttribute('hidden');
      }
    }

    /* ── Thumbnail sync ────────────────────────────────────────── */

    function syncThumbnails(index) {
      if (!thumbSlider) return;
      var mediaId = slides[index] && slides[index].dataset.mediaId;
      if (!mediaId) return;

      thumbSlider.querySelectorAll('.thumbnail').forEach(function (btn) {
        btn.removeAttribute('aria-current');
      });
      var activeItem = thumbSlider.querySelector('[data-target="' + mediaId + '"]');
      if (!activeItem) return;
      var activeBtn = activeItem.querySelector('.thumbnail');
      if (activeBtn) activeBtn.setAttribute('aria-current', 'true');

      // Scroll thumb strip vertically to keep active visible
      var thumbList = thumbSlider.querySelector('.thumbnail-list');
      if (thumbList) {
        var itemTop    = activeItem.offsetTop;
        var itemH      = activeItem.offsetHeight;
        var listTop    = thumbList.scrollTop;
        var listH      = thumbList.offsetHeight;
        if (itemTop < listTop) {
          thumbList.scrollTo({ top: itemTop, behavior: 'smooth' });
        } else if (itemTop + itemH > listTop + listH) {
          thumbList.scrollTo({ top: itemTop + itemH - listH, behavior: 'smooth' });
        }
      }
    }

    /* ── Navigate to a slide ───────────────────────────────────── */

    function goToSlide(index) {
      index = Math.max(0, Math.min(index, slides.length - 1));
      if (index === currentIndex) return;

      // Try clicking the thumbnail button so Dawn handles all its side
      // effects (video play, live region, variant state). The MutationObserver
      // below will pick up the resulting is-active change and apply the transform.
      var targetId  = slides[index].dataset.mediaId;
      var thumbItem = thumbSlider && thumbSlider.querySelector('[data-target="' + targetId + '"]');
      var thumbBtn  = thumbItem && thumbItem.querySelector('button');

      if (thumbBtn) {
        thumbBtn.click();
        // MutationObserver handles transform + arrows after is-active changes
      } else {
        // No thumbnail (single-image or hidden variant) — do it directly
        currentIndex = index;
        slides.forEach(function (s, i) { s.classList.toggle('is-active', i === index); });
        applyTransform(index);
        updateArrows();
        syncThumbnails(index);
      }
    }

    /* ── MutationObserver: react to is-active changes from ANY source
         (thumbnail clicks, variant switching, Dawn's own logic)      */

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName !== 'class') return;
        var el = m.target;
        if (!el.classList.contains('is-active')) return;
        var idx = slides.indexOf(el);
        if (idx < 0 || idx === currentIndex) return;
        currentIndex = idx;
        applyTransform(idx);
        updateArrows();
        // Thumbnail scroll is handled by Dawn's setActiveThumbnail, but
        // that uses horizontal scrollLeft — we redo it vertically here.
        syncThumbnails(idx);
      });
    });

    slides.forEach(function (slide) {
      observer.observe(slide, { attributes: true, attributeFilter: ['class'] });
    });

    /* ── Arrow button clicks ───────────────────────────────────── */

    prevArrow.addEventListener('click', function () {
      goToSlide(currentIndex - 1);
    });

    nextArrow.addEventListener('click', function () {
      goToSlide(currentIndex + 1);
    });

    /* ── Keyboard: left/right when gallery focused ─────────────── */

    mediaGallery.addEventListener('keydown', function (e) {
      if (!MQ_DESKTOP.matches) return;
      if (e.key === 'ArrowLeft')  goToSlide(currentIndex - 1);
      if (e.key === 'ArrowRight') goToSlide(currentIndex + 1);
    });

    /* ── Re-calculate on window resize ────────────────────────── */

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        applyTransform(currentIndex, false); // instant reposition
      }, 100);
    });

    /* ── Initial state ─────────────────────────────────────────── */

    currentIndex = slides.findIndex(function (s) { return s.classList.contains('is-active'); });
    if (currentIndex < 0) currentIndex = 0;
    applyTransform(currentIndex, false);  // jump to position without transition
    updateArrows();
  }

  /* ── Boot ─────────────────────────────────────────────────────── */

  function initAll() {
    document.querySelectorAll('media-gallery').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('media-gallery').forEach(function (el) {
      var wrap = el.querySelector('[data-woke-gallery]');
      if (wrap) delete wrap.dataset.wokeInit;
      initGallery(el);
    });
  });

})();
